import { defaultExpectedMatchingFraction } from "@lib/constants";
import { mvfProfileFromTrancheProfile } from "@lib/tranche";
import {
  assertGetEvaluator,
  assertGetFunder,
  assertGetRecommender,
} from "./accessors";
import type { Action } from "./actions";
import type { RoundAggregate } from "./aggregate";

export function undoAction(
  action: Action,
): ((roundBeforeAction: RoundAggregate) => Action) | null {
  switch (action.type) {
    case "EVALUATION_FEEDBACK_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId, evaluableId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        const { positive, negative } = member.evaluableFeedback[evaluableId]
          ?.positiveNegative ?? { positive: "", negative: "" };
        return {
          type: "EVALUATION_FEEDBACK_CHANGE",
          memberId,
          evaluableId,
          positive,
          negative,
        };
      };
    case "EVALUATION_NOTES_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId, evaluableId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        return {
          type: "EVALUATION_NOTES_CHANGE",
          memberId,
          evaluableId,
          notes: member.evaluableNotes[evaluableId] ?? "",
        };
      };
    case "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId, evaluableId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        return {
          type: "NOTES_AND_FEEDBACK_VISIBILITY_CHANGE",
          memberId,
          evaluableId,
          visibility: member.evaluablePermissions[evaluableId] ?? "default",
        };
      };
    /* TODO
    case "EVALUATION_STATUS_CHANGE": {
      const { evaluatorId, evaluableId } = action;
      const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
      const evaluation = evaluator.evaluations[evaluableId];

      const change =
        evaluation?.state == null
          ? "not-started"
          : evaluation.state === "hidden"
            ? "hide"
            : evaluation.state;

      return {
        type: "EVALUATION_STATUS_CHANGE",
        evaluatorId,
        evaluableId,
        change,
      };
    }
    */

    case "EVALUATION_MVF_CHANGE":
    case "EVALUATION_SET_ZERO":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        const mvf = mvfProfileFromTrancheProfile(evaluator.trancheList)[
          evaluableId
        ];
        return {
          type: "EVALUATION_MVF_CHANGE",
          evaluatorId,
          evaluableId,
          mvf,
        };
      };

    case "SET_EXPECTED_MATCHING_FRACTION":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, evaluableId } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        const evaluation = evaluator.evaluations[evaluableId];
        const expectedMatchingFraction =
          evaluation.expectedMatchingFraction ??
          defaultExpectedMatchingFraction;
        return {
          type: "SET_EXPECTED_MATCHING_FRACTION",
          evaluatorId,
          evaluableId,
          expectedMatchingFraction,
        };
      };

    // TODO: NORMALIZE_EVALUATIONS

    case "SET_TRANCHE_ALLOCATION":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, trancheIndex, evaluableId } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        const tranche = evaluator.trancheList[trancheIndex];
        const allocation = tranche.allocations[evaluableId];

        return {
          type: "SET_TRANCHE_ALLOCATION",
          evaluatorId,
          trancheIndex,
          evaluableId,
          amount: allocation.amount,
        };
      };

    case "SET_TRANCHE_HEIGHT":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, trancheIndex } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        const tranche = evaluator.trancheList[trancheIndex];
        return {
          type: "SET_TRANCHE_HEIGHT",
          evaluatorId,
          trancheIndex,
          height: tranche.height,
        };
      };

    case "INVESTIGATION_INTEREST_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, memberId } = action;
        const investigation =
          roundBeforeAction.investigation[`${proposalId}%${memberId}`];
        const interest = investigation?.interest ?? null;
        const confirmedReviewer = investigation?.confirmedReviewer ?? false;

        return {
          type: "INVESTIGATION_INTEREST_CHANGE",
          proposalId,
          memberId,
          interest,
          confirmedReviewer,
        };
      };

    case "REVIEW_PROGRESS_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, memberId } = action;
        const reviewProgress =
          roundBeforeAction.reviewProgress[`${proposalId}%${memberId}`];
        const progress = reviewProgress ?? null;

        return {
          type: "REVIEW_PROGRESS_CHANGE",
          proposalId,
          memberId,
          progress,
        };
      };

    case "PROPOSAL_DISCLOSURE_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, userId } = action;
        const proposalDisclosure =
          roundBeforeAction.proposalDisclosure[`${proposalId}%${userId}`];
        const hasDisclosure = proposalDisclosure?.hasDisclosure ?? false;
        const selfRecused = proposalDisclosure?.selfRecused ?? false;
        const disclosure = proposalDisclosure?.disclosure ?? "";

        return {
          type: "PROPOSAL_DISCLOSURE_CHANGE",
          proposalId,
          userId,
          hasDisclosure,
          selfRecused,
          disclosure,
        };
      };

    case "RECUSAL_VOTE":
      return (roundBeforeAction: RoundAggregate) => {
        const { proposalId, votingUserId, subjectUserId } = action;
        const disclosure =
          roundBeforeAction.proposalDisclosure[
            `${proposalId}%${subjectUserId}`
          ];
        const recusalVote = disclosure?.votes[votingUserId] ?? "a";

        return {
          type: "RECUSAL_VOTE",
          proposalId,
          votingUserId,
          subjectUserId,
          vote: recusalVote,
        };
      };

    case "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId } = action;
        const recommender = assertGetRecommender(
          roundBeforeAction,
          evaluatorId,
        );
        const outsideDollarSubstitutionRate =
          recommender.outsideDollarSubstitutionRate;

        return {
          type: "RECOMMENDER_OUTSIDE_DOLLAR_SUBSTITUTION_RATE_CHANGE",
          evaluatorId,
          outsideDollarSubstitutionRate,
        };
      };

    case "ADD_TRANCHE":
      return () => {
        const { evaluatorId, trancheIndex } = action;
        return { type: "REMOVE_TRANCHE", evaluatorId, trancheIndex };
      };

    case "REMOVE_TRANCHE":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, trancheIndex } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        const tranche = evaluator.trancheList[trancheIndex];
        return {
          type: "ADD_TRANCHE",
          evaluatorId,
          trancheIndex,
          trancheHeight: tranche.height,
          trancheAllocations: tranche.allocations,
        };
      };

    case "ROUND_MEMBER_PROPERTY_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { memberId } = action;
        const member = roundBeforeAction.members[memberId];
        if (!member) {
          throw new Error("Member not found");
        }
        return {
          type: "ROUND_MEMBER_PROPERTY_CHANGE",
          memberId,
          feedbackVisibleByDefault: member.feedbackVisibleByDefault,
        };
      };

    case "EVALUATOR_PROPERTY_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { evaluatorId, property } = action;
        const evaluator = assertGetEvaluator(roundBeforeAction, evaluatorId);
        return {
          type: "EVALUATOR_PROPERTY_CHANGE",
          evaluatorId,
          property,
          value: evaluator.shortname,
        };
      };

    case "FUNDER_BUDGET_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_BUDGET_CHANGE",
          funderId,
          budget: funder.budget,
        };
      };

    case "FUNDER_SETTLEMENT_BUDGET_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        return {
          type: "FUNDER_SETTLEMENT_BUDGET_CHANGE",
          funderId,
          budget: funder.speculationGrantSettlementBudget ?? 0,
        };
      };

    case "FUNDER_SCORES_CHANGE":
      return (roundBeforeAction: RoundAggregate) => {
        const { funderId, scores } = action;
        const funder = assertGetFunder(roundBeforeAction, funderId);
        const prevScores = scores.map(({ recommenderId, proposalId }) => ({
          recommenderId,
          proposalId,
          score: funder.scores?.[recommenderId]?.[proposalId],
        }));
        return { type: "FUNDER_SCORES_CHANGE", funderId, scores: prevScores };
      };

    case "INCREMENTAL_EVALS_PUSH":
      return () => ({
        type: "INCREMENTAL_EVALS_UNDO",
        evaluatorId: action.evaluatorId,
      });

    case "INCREMENTAL_EVALS_UNDO":
      return () => ({
        type: "INCREMENTAL_EVALS_REDO",
        evaluatorId: action.evaluatorId,
      });

    case "INCREMENTAL_EVALS_REDO":
      return () => ({
        type: "INCREMENTAL_EVALS_UNDO",
        evaluatorId: action.evaluatorId,
      });

    default:
      return null;
  }
}
